import { useState, useEffect } from 'react'
import { Rss, Save, Check } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { Label } from '@/components/ui/label'
import { USMap, US_STATES } from '@/components/us-map'
import { USAInteractiveMap } from '@/components/usa-interactive-map'
import { dashboardApi, newsApi } from '@/services/api'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { motion } from 'framer-motion'

export function DashboardFeed() {
  const queryClient = useQueryClient()
  
  // State
  const [sortType, setSortType] = useState('alphabetical') // 'alphabetical', 'byState' - default to alphabetical
  const [viewType, setViewType] = useState('map') // 'map', 'list', 'grid'
  const [selectedState, setSelectedState] = useState(null)
  const [selectedFeeds, setSelectedFeeds] = useState([])

  // Fetch subscription and feeds
  const { data: subscription, isLoading: loadingSubscription } = useQuery({
    queryKey: ['subscription'],
    queryFn: async () => {
      const response = await dashboardApi.getSubscribedFeeds()
      return response.data
    },
    onError: () => {
      toast.error('Failed to load subscription')
    }
  })

  // Fetch all providers (to show premium feeds user doesn't have)
  const { data: allProviders } = useQuery({
    queryKey: ['allProviders'],
    queryFn: async () => {
      const response = await newsApi.getProviders()
      return response.data
    }
  })

  // Initialize selected feeds from subscription
  useEffect(() => {
    if (subscription?.display_feeds) {
      // display_feeds contains the selected feeds
      setSelectedFeeds(subscription.display_feeds)
    }
  }, [subscription])

  // Save mutation
  const saveMutation = useMutation({
    mutationFn: (feeds) => dashboardApi.updateDisplayFeeds(feeds),
    onSuccess: () => {
      queryClient.invalidateQueries(['subscription'])
      toast.success('Feed preferences saved!')
    },
    onError: () => {
      toast.error('Failed to save preferences')
    }
  })

  // Get all available feeds (from subscription)
  const subscribedFeeds = subscription?.feeds || []
  const allFeeds = allProviders || []
  
  // Categorize feeds into three tiers
  const freeFeeds = allFeeds.filter(feed => feed.non_profit_org)
  const basicFeeds = allFeeds.filter(feed => !feed.non_profit_org && feed.basic_feed)
  const premiumFeeds = allFeeds.filter(feed => !feed.non_profit_org && !feed.basic_feed)
  
  // Check if user has access to a feed
  const hasAccessToFeed = (feedAlias) => {
    // Find the feed in allFeeds
    const feed = allFeeds.find(f => f.alias === feedAlias)
    if (!feed) return false
    
    // Free/Non-profit feeds are ALWAYS accessible
    if (feed.non_profit_org) return true
    
    // For paid feeds, check if it's in user's subscription
    return subscribedFeeds.some(subFeed => subFeed.alias === feedAlias)
  }
  
  // Get user's plan name
  const userPlan = subscription?.plan?.name || 'Free'
  
  // Calculate feed counts per state
  const feedCounts = {}
  US_STATES.forEach(state => {
    feedCounts[state.id] = allFeeds.filter(feed => feed.state === state.id).length
  })

  // Check if feed is selected
  const isFeedSelected = (feedAlias) => {
    return selectedFeeds.some(feed => feed.alias === feedAlias || feed === feedAlias)
  }

  // Handle individual feed toggle (SECURITY: Check access before adding)
  const toggleFeed = (feed) => {
    if (isFeedSelected(feed.alias)) {
      setSelectedFeeds(selectedFeeds.filter(f => 
        (f.alias || f) !== feed.alias
      ))
    } else {
      // SECURITY: Only allow selecting feeds user has access to
      if (hasAccessToFeed(feed.alias)) {
        setSelectedFeeds([...selectedFeeds, feed])
      } else {
        toast.error('You need to upgrade your subscription to access this feed')
      }
    }
  }

  // Handle state-level selection (SECURITY: Only select feeds user has access to)
  const toggleStateFeeds = (stateId) => {
    const feedsInState = allFeeds.filter(feed => feed.state === stateId)
    // SECURITY: Only include feeds the user has access to
    const accessibleFeedsInState = feedsInState.filter(feed => hasAccessToFeed(feed.alias))
    const allAccessibleSelected = accessibleFeedsInState.every(feed => isFeedSelected(feed.alias))
    
    if (allAccessibleSelected) {
      // Deselect all accessible feeds in this state
      setSelectedFeeds(selectedFeeds.filter(selected => 
        !accessibleFeedsInState.some(stateFeed => stateFeed.alias === (selected.alias || selected))
      ))
    } else {
      // Select only accessible feeds in this state that aren't already selected
      const newFeeds = accessibleFeedsInState.filter(feed => !isFeedSelected(feed.alias))
      setSelectedFeeds([...selectedFeeds, ...newFeeds])
    }
  }

  // Check if all accessible feeds in a state are selected
  const isStateSelected = (stateId) => {
    const feedsInState = allFeeds.filter(feed => feed.state === stateId)
    // SECURITY: Only check accessible feeds
    const accessibleFeedsInState = feedsInState.filter(feed => hasAccessToFeed(feed.alias))
    if (accessibleFeedsInState.length === 0) return false
    return accessibleFeedsInState.every(feed => isFeedSelected(feed.alias))
  }

  // Get feeds for selected state
  const getStateFeeds = (stateName) => {
    const state = US_STATES.find(s => s.name === stateName)
    if (!state) return []
    return allFeeds.filter(feed => feed.state === state.id)
  }

  // Get state ID by name
  const getStateIdByName = (stateName) => {
    const state = US_STATES.find(s => s.name === stateName)
    return state?.id
  }

  // Sort feeds alphabetically by category
  const sortedFreeFeeds = [...freeFeeds].sort((a, b) => 
    a.display_name.localeCompare(b.display_name)
  )
  const sortedBasicFeeds = [...basicFeeds].sort((a, b) => 
    a.display_name.localeCompare(b.display_name)
  )
  const sortedPremiumFeeds = [...premiumFeeds].sort((a, b) => 
    a.display_name.localeCompare(b.display_name)
  )

  // Group feeds by state
  const groupedByState = US_STATES.map(state => ({
    ...state,
    feeds: allFeeds.filter(feed => feed.state === state.id)
  })).filter(state => state.feeds.length > 0)

  // Handle save (SECURITY: Final validation before sending to backend)
  const handleSave = () => {
    // SECURITY: Filter out any feeds user doesn't have access to
    const validFeeds = selectedFeeds.filter(feed => {
      const feedAlias = feed.alias || feed
      return hasAccessToFeed(feedAlias)
    })
    
    // Backend expects array of objects with 'alias' property
    const feedsToSave = validFeeds.map(feed => ({
      alias: feed.alias || feed
    }))
    
    // Warn if some feeds were filtered out
    if (validFeeds.length < selectedFeeds.length) {
      toast.warning(`${selectedFeeds.length - validFeeds.length} feed(s) removed (subscription required)`)
      setSelectedFeeds(validFeeds) // Update UI to reflect valid feeds only
    }
    
    saveMutation.mutate(feedsToSave)
  }

  if (loadingSubscription) {
    return (
      <div className="w-full">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </div>
    )
  }

  return (
    <div className="w-full">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2 flex items-center gap-3">
          <Rss className="w-8 h-8 text-primary" />
          My Feed
        </h1>
        <p className="text-gray-600">
          Customize your news feed by selecting sources
        </p>
      </div>

      {/* Stats with Save Button */}
      <div className="bg-gradient-to-r from-gray-50 to-slate-50 rounded-xl p-6 mb-6 border border-slate-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-8">
            <div>
              <p className="text-sm text-gray-600 mb-1">Selected Feeds</p>
              <p className="text-3xl font-bold text-gray-900">
                {selectedFeeds.length} / {allFeeds.length}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-600 mb-1">Available States</p>
              <p className="text-3xl font-bold text-gray-900">
                {Object.values(feedCounts).filter(count => count > 0).length}
              </p>
            </div>
          </div>
          <Button 
            onClick={handleSave} 
            disabled={saveMutation.isLoading}
            className="flex items-center gap-2 bg-primary hover:bg-primary"
            size="lg"
          >
            {saveMutation.isLoading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                Save Changes
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Sorting Options */}
      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 mb-6">
        <h3 className="font-semibold text-gray-900 mb-4">Sort & Filter</h3>
        
        <div className="space-y-4">
          {/* Main sorting options */}
          <RadioGroup value={sortType} onValueChange={(value) => {
            setSortType(value)
            setSelectedState(null)
          }}>
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2">
                <RadioGroupItem value="alphabetical" id="alphabetical" />
                <Label htmlFor="alphabetical" className="text-sm font-medium cursor-pointer">
                  Alphabetical
                </Label>
              </div>

              <div className="flex items-center gap-2">
                <RadioGroupItem value="byState" id="byState" />
                <Label htmlFor="byState" className="text-sm font-medium cursor-pointer">
                  By State
                </Label>
              </div>
            </div>
          </RadioGroup>

          {/* View type for By State */}
          {sortType === 'byState' && (
            <RadioGroup value={viewType} onValueChange={setViewType} className="ml-6">
              <div className="flex items-center gap-6">
                <div className="flex items-center gap-2">
                  <RadioGroupItem value="map" id="map" />
                  <Label htmlFor="map" className="text-sm cursor-pointer">
                    Map View
                  </Label>
                </div>
                <div className="flex items-center gap-2">
                  <RadioGroupItem value="grid" id="grid" />
                  <Label htmlFor="grid" className="text-sm cursor-pointer">
                    Grid View
                  </Label>
                </div>
                <div className="flex items-center gap-2">
                  <RadioGroupItem value="list" id="list" />
                  <Label htmlFor="list" className="text-sm cursor-pointer">
                    List View
                  </Label>
                </div>
              </div>
            </RadioGroup>
          )}
        </div>
      </div>

      {/* Alphabetical View */}
      {sortType === 'alphabetical' && (
        <div className="space-y-6">
          {/* Free/Non-Profit Feeds */}
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
            <div className="flex items-center gap-2 mb-4">
              <h3 className="font-semibold text-gray-900">Free & Non-Profit Feeds</h3>
              <span className="px-2 py-1 bg-green-100 text-green-700 text-xs font-medium rounded-full">
                {sortedFreeFeeds.length} Available
              </span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 max-h-96 overflow-y-auto">
              {sortedFreeFeeds.map((feed) => (
                <motion.div
                  key={feed.alias}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 border border-gray-200"
                >
                  <Checkbox
                    id={feed.alias}
                    checked={isFeedSelected(feed.alias)}
                    onCheckedChange={() => toggleFeed(feed)}
                  />
                  <Label 
                    htmlFor={feed.alias} 
                    className="flex-1 cursor-pointer text-sm text-gray-700"
                  >
                    {feed.display_name}
                    <span className="ml-2 text-xs text-green-600">(Free)</span>
                  </Label>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Basic Feeds */}
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
            <div className="flex items-center gap-2 mb-4">
              <h3 className="font-semibold text-gray-900">Basic Feeds</h3>
              <span className="px-2 py-1 bg-slate-100 text-primary text-xs font-medium rounded-full">
                {sortedBasicFeeds.length} Total
              </span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 max-h-96 overflow-y-auto">
              {sortedBasicFeeds.map((feed) => {
                const hasAccess = hasAccessToFeed(feed.alias)
                return (
                  <motion.div
                    key={feed.alias}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className={`flex items-center gap-3 p-3 rounded-lg border ${
                      hasAccess 
                        ? 'hover:bg-gray-50 border-gray-200' 
                        : 'bg-gray-50 border-gray-300 opacity-60'
                    }`}
                    title={!hasAccess ? 'Upgrade to Basic plan to access' : ''}
                  >
                    <div className="relative">
                      <Checkbox
                        id={feed.alias}
                        checked={isFeedSelected(feed.alias)}
                        onCheckedChange={() => hasAccess && toggleFeed(feed)}
                        disabled={!hasAccess}
                      />
                      {!hasAccess && (
                        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                          <span className="text-orange-600 text-lg font-bold">✖</span>
                        </div>
                      )}
                    </div>
                    <Label 
                      htmlFor={feed.alias} 
                      className={`flex-1 text-sm ${
                        hasAccess ? 'cursor-pointer text-gray-900' : 'cursor-not-allowed text-gray-500'
                      }`}
                    >
                      {feed.display_name}
                      {!hasAccess && (
                        <span className="ml-2 text-xs text-orange-600">(Basic)</span>
                      )}
                    </Label>
                  </motion.div>
                )
              })}
            </div>
          </div>

          {/* Premium Feeds */}
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
            <div className="flex items-center gap-2 mb-4">
              <h3 className="font-semibold text-gray-900">Premium Feeds</h3>
              <span className="px-2 py-1 bg-purple-100 text-purple-700 text-xs font-medium rounded-full">
                {sortedPremiumFeeds.length} Total
              </span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 max-h-96 overflow-y-auto">
              {sortedPremiumFeeds.map((feed) => {
                const hasAccess = hasAccessToFeed(feed.alias)
                return (
                  <motion.div
                    key={feed.alias}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className={`flex items-center gap-3 p-3 rounded-lg border ${
                      hasAccess 
                        ? 'hover:bg-gray-50 border-gray-200' 
                        : 'bg-gray-50 border-gray-300 opacity-60'
                    }`}
                    title={!hasAccess ? 'Upgrade to Premium plan to access' : ''}
                  >
                    <div className="relative">
                      <Checkbox
                        id={feed.alias}
                        checked={isFeedSelected(feed.alias)}
                        onCheckedChange={() => hasAccess && toggleFeed(feed)}
                        disabled={!hasAccess}
                      />
                      {!hasAccess && (
                        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                          <span className="text-red-600 text-lg font-bold">✖</span>
                        </div>
                      )}
                    </div>
                    <Label 
                      htmlFor={feed.alias} 
                      className={`flex-1 text-sm ${
                        hasAccess ? 'cursor-pointer text-gray-900' : 'cursor-not-allowed text-gray-500'
                      }`}
                    >
                      {feed.display_name}
                      {!hasAccess && (
                        <span className="ml-2 text-xs text-red-600">(Premium)</span>
                      )}
                    </Label>
                  </motion.div>
                )
              })}
            </div>
          </div>
        </div>
      )}

      {/* By State - Map View (SVG Interactive) */}
      {sortType === 'byState' && viewType === 'map' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <USAInteractiveMap
              selectedState={selectedState}
              onStateSelect={setSelectedState}
            />
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-6">
            {selectedState ? (
              <>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-gray-900">{selectedState}</h3>
                  {getStateFeeds(selectedState).length > 0 && (
                    <Checkbox
                      id="select-all-state"
                      checked={isStateSelected(getStateIdByName(selectedState))}
                      onCheckedChange={() => toggleStateFeeds(getStateIdByName(selectedState))}
                    />
                  )}
                </div>
                {getStateFeeds(selectedState).length > 0 ? (
                  <div className="space-y-4 max-h-96 overflow-y-auto">
                    {/* Free Feeds */}
                    {getStateFeeds(selectedState).filter(f => f.non_profit_org).length > 0 && (
                      <div>
                        <h4 className="text-xs font-semibold text-green-700 mb-2">Free & Non-Profit</h4>
                        <div className="space-y-2">
                          {getStateFeeds(selectedState).filter(f => f.non_profit_org).map((feed) => (
                            <div key={feed.alias} className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 border border-gray-200">
                              <Checkbox id={`state-${feed.alias}`} checked={isFeedSelected(feed.alias)} onCheckedChange={() => toggleFeed(feed)} />
                              <Label htmlFor={`state-${feed.alias}`} className="flex-1 cursor-pointer text-sm text-gray-700">
                                {feed.display_name}<span className="ml-2 text-xs text-green-600">(Free)</span>
                              </Label>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Basic Feeds */}
                    {getStateFeeds(selectedState).filter(f => !f.non_profit_org && f.basic_feed).length > 0 && (
                      <div>
                        <h4 className="text-xs font-semibold text-primary mb-2">Basic</h4>
                        <div className="space-y-2">
                          {getStateFeeds(selectedState).filter(f => !f.non_profit_org && f.basic_feed).map((feed) => {
                            const hasAccess = hasAccessToFeed(feed.alias)
                            return (
                              <div key={feed.alias} className={`flex items-center gap-3 p-3 rounded-lg border ${hasAccess ? 'hover:bg-gray-50 border-gray-200' : 'bg-gray-50 border-gray-300 opacity-60'}`} title={!hasAccess ? 'Upgrade to Basic plan' : ''}>
                                <div className="relative">
                                  <Checkbox id={`state-${feed.alias}`} checked={isFeedSelected(feed.alias)} onCheckedChange={() => hasAccess && toggleFeed(feed)} disabled={!hasAccess} />
                                  {!hasAccess && <div className="absolute inset-0 flex items-center justify-center pointer-events-none"><span className="text-orange-600 text-lg font-bold">✖</span></div>}
                                </div>
                                <Label htmlFor={`state-${feed.alias}`} className={`flex-1 text-sm ${hasAccess ? 'cursor-pointer text-gray-900' : 'cursor-not-allowed text-gray-500'}`}>
                                  {feed.display_name}{!hasAccess && <span className="ml-2 text-xs text-orange-600">(Basic)</span>}
                                </Label>
                              </div>
                            )
                          })}
                        </div>
                      </div>
                    )}
                    
                    {/* Premium Feeds */}
                    {getStateFeeds(selectedState).filter(f => !f.non_profit_org && !f.basic_feed).length > 0 && (
                      <div>
                        <h4 className="text-xs font-semibold text-purple-700 mb-2">Premium</h4>
                        <div className="space-y-2">
                          {getStateFeeds(selectedState).filter(f => !f.non_profit_org && !f.basic_feed).map((feed) => {
                            const hasAccess = hasAccessToFeed(feed.alias)
                            return (
                              <div key={feed.alias} className={`flex items-center gap-3 p-3 rounded-lg border ${hasAccess ? 'hover:bg-gray-50 border-gray-200' : 'bg-gray-50 border-gray-300 opacity-60'}`} title={!hasAccess ? 'Upgrade to Premium plan' : ''}>
                                <div className="relative">
                                  <Checkbox id={`state-${feed.alias}`} checked={isFeedSelected(feed.alias)} onCheckedChange={() => hasAccess && toggleFeed(feed)} disabled={!hasAccess} />
                                  {!hasAccess && <div className="absolute inset-0 flex items-center justify-center pointer-events-none"><span className="text-red-600 text-lg font-bold">✖</span></div>}
                                </div>
                                <Label htmlFor={`state-${feed.alias}`} className={`flex-1 text-sm ${hasAccess ? 'cursor-pointer text-gray-900' : 'cursor-not-allowed text-gray-500'}`}>
                                  {feed.display_name}{!hasAccess && <span className="ml-2 text-xs text-red-600">(Premium)</span>}
                                </Label>
                              </div>
                            )
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Rss className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-sm font-medium text-gray-700 mb-1">No providers available</p>
                    <p className="text-xs text-gray-500">This state has no news feeders yet</p>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-12">
                <Rss className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-sm font-medium text-gray-700 mb-1">Select a state</p>
                <p className="text-xs text-gray-500">Click a state on the map to view feeds</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* By State - Grid View (Grid of state buttons) */}
      {sortType === 'byState' && viewType === 'grid' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <USMap
              selectedState={selectedState}
              onStateSelect={setSelectedState}
              feedCounts={feedCounts}
            />
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-6">
            {selectedState ? (
              <>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-gray-900">{selectedState}</h3>
                  {getStateFeeds(selectedState).length > 0 && (
                    <Checkbox
                      id="select-all-state-grid"
                      checked={isStateSelected(getStateIdByName(selectedState))}
                      onCheckedChange={() => toggleStateFeeds(getStateIdByName(selectedState))}
                    />
                  )}
                </div>
                {getStateFeeds(selectedState).length > 0 ? (
                  <div className="space-y-4 max-h-96 overflow-y-auto">
                    {/* Free Feeds in State */}
                    {getStateFeeds(selectedState).filter(f => f.non_profit_org).length > 0 && (
                      <div>
                        <h4 className="text-xs font-semibold text-green-700 mb-2">Free & Non-Profit</h4>
                        <div className="space-y-2">
                          {getStateFeeds(selectedState).filter(f => f.non_profit_org).map((feed) => (
                            <div
                              key={feed.alias}
                              className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 border border-gray-200"
                            >
                              <Checkbox
                                id={`grid-${feed.alias}`}
                                checked={isFeedSelected(feed.alias)}
                                onCheckedChange={() => toggleFeed(feed)}
                              />
                              <Label 
                                htmlFor={`grid-${feed.alias}`} 
                                className="flex-1 cursor-pointer text-sm text-gray-700"
                              >
                                {feed.display_name}
                                <span className="ml-2 text-xs text-green-600">(Free)</span>
                              </Label>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Premium Feeds in State */}
                    {getStateFeeds(selectedState).filter(f => !f.non_profit_org).length > 0 && (
                      <div>
                        <h4 className="text-xs font-semibold text-primary mb-2">Premium</h4>
                        <div className="space-y-2">
                          {getStateFeeds(selectedState).filter(f => !f.non_profit_org).map((feed) => {
                            const hasAccess = hasAccessToFeed(feed.alias)
                            return (
                              <div
                                key={feed.alias}
                                className={`flex items-center gap-3 p-3 rounded-lg border ${
                                  hasAccess 
                                    ? 'hover:bg-gray-50 border-gray-200' 
                                    : 'bg-gray-50 border-gray-300 opacity-60'
                                }`}
                                title={!hasAccess ? 'Upgrade subscription to access' : ''}
                              >
                                <div className="relative">
                                  <Checkbox
                                    id={`grid-${feed.alias}`}
                                    checked={isFeedSelected(feed.alias)}
                                    onCheckedChange={() => hasAccess && toggleFeed(feed)}
                                    disabled={!hasAccess}
                                  />
                                  {!hasAccess && (
                                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                                      <span className="text-red-600 text-lg font-bold">✖</span>
                                    </div>
                                  )}
                                </div>
                                <Label 
                                  htmlFor={`grid-${feed.alias}`} 
                                  className={`flex-1 text-sm ${
                                    hasAccess ? 'cursor-pointer text-gray-900' : 'cursor-not-allowed text-gray-500'
                                  }`}
                                >
                                  {feed.display_name}
                                  {!hasAccess && (
                                    <span className="ml-2 text-xs text-red-600">(Premium)</span>
                                  )}
                                </Label>
                              </div>
                            )
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Rss className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-sm font-medium text-gray-700 mb-1">No providers available</p>
                    <p className="text-xs text-gray-500">This state has no news feeders yet</p>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-12">
                <Rss className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-sm font-medium text-gray-700 mb-1">Select a state</p>
                <p className="text-xs text-gray-500">Click a state button to view feeds</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* By State - List View */}
      {sortType === 'byState' && viewType === 'list' && (
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
          <h3 className="font-semibold text-gray-900 mb-4">Feeds by State</h3>
          <div className="space-y-6 max-h-96 overflow-y-auto">
            {groupedByState.map((state) => {
              const stateFreeFeeds = state.feeds.filter(f => f.non_profit_org)
              const statePremiumFeeds = state.feeds.filter(f => !f.non_profit_org)
              
              return (
                <div key={state.id} className="border-b border-gray-200 pb-4 last:border-0">
                  <div className="flex items-center gap-3 mb-3">
                    <Checkbox
                      id={`state-${state.id}`}
                      checked={isStateSelected(state.id)}
                      onCheckedChange={() => toggleStateFeeds(state.id)}
                    />
                    <Label 
                      htmlFor={`state-${state.id}`} 
                      className="font-semibold cursor-pointer"
                    >
                      {state.name} ({state.feeds.length})
                    </Label>
                  </div>
                  
                  <div className="ml-6 space-y-4">
                    {/* Free Feeds */}
                    {stateFreeFeeds.length > 0 && (
                      <div>
                        <h4 className="text-xs font-semibold text-green-700 mb-2">Free & Non-Profit</h4>
                        <div className="space-y-2">
                          {stateFreeFeeds.map((feed) => (
                            <div
                              key={feed.alias}
                              className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50 border border-gray-200"
                            >
                              <Checkbox
                                id={`list-${feed.alias}`}
                                checked={isFeedSelected(feed.alias)}
                                onCheckedChange={() => toggleFeed(feed)}
                              />
                              <Label 
                                htmlFor={`list-${feed.alias}`} 
                                className="flex-1 cursor-pointer text-sm text-gray-700"
                              >
                                {feed.display_name}
                                <span className="ml-2 text-xs text-green-600">(Free)</span>
                              </Label>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Premium Feeds */}
                    {statePremiumFeeds.length > 0 && (
                      <div>
                        <h4 className="text-xs font-semibold text-primary mb-2">Premium</h4>
                        <div className="space-y-2">
                          {statePremiumFeeds.map((feed) => {
                            const hasAccess = hasAccessToFeed(feed.alias)
                            return (
                              <div
                                key={feed.alias}
                                className={`flex items-center gap-3 p-2 rounded-lg border ${
                                  hasAccess 
                                    ? 'hover:bg-gray-50 border-gray-200' 
                                    : 'bg-gray-50 border-gray-300 opacity-60'
                                }`}
                                title={!hasAccess ? 'Upgrade subscription to access' : ''}
                              >
                                <div className="relative">
                                  <Checkbox
                                    id={`list-${feed.alias}`}
                                    checked={isFeedSelected(feed.alias)}
                                    onCheckedChange={() => hasAccess && toggleFeed(feed)}
                                    disabled={!hasAccess}
                                  />
                                  {!hasAccess && (
                                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                                      <span className="text-red-600 text-lg font-bold">✖</span>
                                    </div>
                                  )}
                                </div>
                                <Label 
                                  htmlFor={`list-${feed.alias}`} 
                                  className={`flex-1 text-sm ${
                                    hasAccess ? 'cursor-pointer text-gray-900' : 'cursor-not-allowed text-gray-500'
                                  }`}
                                >
                                  {feed.display_name}
                                  {!hasAccess && (
                                    <span className="ml-2 text-xs text-red-600">(Premium)</span>
                                  )}
                                </Label>
                              </div>
                            )
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}
